// What the reader returns for each kind of document (RK-SYS-004 section 5.1). Money is in rand as
// printed; the pipeline turns it into whole cents. The bank account number is used only for the
// fingerprint and never stored (A-08).
import { z } from "zod";

// Decision 137 item 23: funds_release_note, Wildswinkel's "Funds Release Note": evidence that the animals of a lot were
// delivered and accepted (the auction house releases the seller's funds on that). Delivery evidence for the lot's invoice;
// never a payment and never an invoice.
export const DOC_TYPES = ["purchase_invoice", "credit_note", "delivery_note", "proof_of_payment", "capture_evidence", "photo", "voice_note", "seller_invoice", "kml_map", "funds_release_note", "other"] as const;
export type DocType = (typeof DOC_TYPES)[number];

const text = z.string().default("");
const party = z.object({ name: text, vat_no: text.optional().default("") });
const contact = z.object({ name: text, phone: text.optional().default(""), email: text.optional().default(""), vat_no: text.optional().default("") });
const rand = z.number().finite();
const count = z.number().int().min(0);
/**
 * Decision 77 7(b) b1: the 1-based page a fact is printed on (an invoice's totals, a lot, a payment). Optional and
 * nullable, so readings stored before pages were read still parse; the pipeline drops a page that cannot be on the
 * document, and a fact without its page on a document of more than one page is flagged on its card, never guessed.
 */
const page = z.number().int().nullable().optional();
/** What every reading ends with: how many pages were read, the page this reading is from, the reader's confidence and its notes. */
const readingTail = {
  pages_read: z.number().int().min(0),
  page,
  confidence: z.number().min(0).max(1),
  notes: text.optional().default("")
};
/**
 * Decision 80 #3: a date that is not printed is read as "" (a "<invoice> Lot.pdf" extract of an invoice's lot lines, a
 * delivery note whose DATE field is blank). A blank is "not printed", never a schema error; the pipeline asks for it or
 * takes the document as a copy of an invoice on file, and never fills it in. A date that is printed is held to
 * YYYY-MM-DD exactly as before: the pattern is not loosened for any value that is not blank.
 */
const dateOrBlank = z.string().refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), { message: "is not a date (YYYY-MM-DD)" });
/**
 * Decision 137 item 20 (with Decision 113 #2): a printed date is held to YYYY-MM-DD exactly; the normaliser
 * (normaliseExtractionDates) does the work of reading every common form before the check, so the pattern is never
 * loosened. An invoice date that is not printed ("") is read through undatedInvoiceExtractionSchema below, by the
 * pipeline alone, which then applies Decision 113 #2 (the auction date, a date a person gave, or a date question).
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const classificationSchema = z.object({
  doc_type: z.enum(DOC_TYPES),
  confidence: z.number().min(0).max(1),
  reason: text.optional(),
  /** Owner's decision 47 (b): the file holds more than one document; the pipeline splits it (Decision 56). */
  several_documents: z.boolean().optional(),
  /**
   * Decision 56 §1: when the file holds several documents, the parts it is split into, in page order. Each part is a
   * kind of document and the 1-based inclusive range of pages it covers. Optional, so a single-document classification
   * is unchanged (no `documents`).
   */
  documents: z.array(z.object({
    doc_type: z.enum(DOC_TYPES),
    page_from: z.number().int().min(1),
    page_to: z.number().int().min(1)
  })).optional()
});
export type Classification = z.infer<typeof classificationSchema>;

export const invoiceLineSchema = z.object({
  lot_number: z.string().min(1),
  species_raw: z.string().min(1),
  males: count,
  females: count,
  unknown_sex: count,
  unit_price_excl: rand.nullable().optional().default(null),
  price_excl: rand,
  vat_rate: z.number().min(0).max(1),
  description_raw: text,
  seller: contact.optional().default({ name: "", phone: "", email: "", vat_no: "" }),
  tags_raw: z.array(z.string()).optional().default([]),
  page
});

const invoiceShape = {
  doc_type: z.literal("purchase_invoice"),
  issuer: party,
  invoice_number: z.string().min(1),
  invoice_date: isoDate,
  /**
   * Decision 113 #2: the auction's date as printed ("" when none is printed), and whether invoice_date is that date
   * because no invoice date is printed (2b). Optional, so readings stored before they existed still parse ("", false).
   */
  auction_date: dateOrBlank.optional().default(""),
  auction_date_used: z.boolean().optional().default(false),
  auction_name: text,
  addressed_to: party,
  terms_wording: text,
  // Decision 80 #3 round 2: an invoice needs at least one lot (checked below); a document with no date printed and no
  // lots is read as it is and held by the pipeline, never taken as a copy of the invoice on file.
  lines: z.array(invoiceLineSchema),
  total_excl: rand,
  vat: rand,
  total_incl: rand,
  balance_due: rand.nullable().optional().default(null),
  /** The bank and branch code printed for payment (not secret; checked against payment notices, BD-81). */
  bank_name: text.optional().default(""),
  bank_branch_code: text.optional().default(""),
  bank_account_number_for_fingerprint_only: text.optional().default(""),
  ...readingTail
};
export const invoiceExtractionSchema = z.object(invoiceShape).superRefine((x, ctx) => {
  if (x.invoice_date !== "" && x.lines.length < 1) ctx.addIssue({ code: "custom", path: ["lines"], message: "An invoice needs at least one lot." });
});
/**
 * Decision 80 #3 and Decision 113 #2c: a purchase invoice whose date is not printed (invoice_date ""), as the "<invoice>
 * Lot.pdf" extracts are. Only the pipeline reads with it, after extractionSchema refused the blank, so the blank is
 * "not printed" (held: a copy of the invoice on file, or a date question), never a schema fault and never a date made up.
 */
export const undatedInvoiceExtractionSchema = z.object({ ...invoiceShape, invoice_date: z.literal("") });
// Decision 113 #2: auction_date and auction_date_used are optional in the type too, so a reading made before they existed
// is still an InvoiceExtraction (the schema gives them "" and false when it parses one).
export type InvoiceExtraction = Omit<z.infer<typeof invoiceExtractionSchema>, "auction_date" | "auction_date_used"> & { auction_date?: string; auction_date_used?: boolean };

export const creditNoteExtractionSchema = z.object({
  doc_type: z.literal("credit_note"),
  issuer: party,
  credit_number: z.string().min(1),
  credit_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  auction_name: text,
  invoice_reference: text.optional().default(""),
  addressed_to: party,
  // Decision 88 #1: each credit line keeps its printed price per animal (unit_price_excl), as on an invoice line.
  lines: z.array(invoiceLineSchema.omit({ tags_raw: true })).min(1),
  total_excl: rand,
  vat: rand,
  total_incl: rand,
  ...readingTail
});
export type CreditNoteExtraction = z.infer<typeof creditNoteExtractionSchema>;

export const sellerInvoiceExtractionSchema = z.object({
  doc_type: z.literal("seller_invoice"),
  seller: contact,
  document_number: z.string().min(1),
  document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  addressed_to: party,
  lines: z.array(z.object({ lot_number: z.string().min(1), species_raw: text, sex: z.enum(["M", "F", "U"]).default("U"), price_excl: rand, description_raw: text })).min(1),
  commission_excl: rand.nullable().optional().default(null),
  total_excl: rand,
  vat: rand,
  total_incl: rand,
  bank_account_number_for_fingerprint_only: text.optional().default(""),
  ...readingTail
});
export type SellerInvoiceExtraction = z.infer<typeof sellerInvoiceExtractionSchema>;

export const deliveryNoteExtractionSchema = z.object({
  doc_type: z.literal("delivery_note"),
  issuer: party,
  note_number: text,
  delivery_date: dateOrBlank,
  invoice_reference: text.optional().default(""),
  signature_present: z.boolean(),
  items: z.array(z.object({
    lot_number: text, species_raw: text, sex: z.enum(["M", "F", "U"]).default("U"), tag: text.optional().default(""), count: z.number().int().min(1).default(1),
    arrived: z.enum(["alive", "dead"]).default("alive"), accepted: z.enum(["yes", "no", "unknown"]).default("unknown"), reason: text.optional().default("")
  })).min(1),
  ...readingTail
});
export type DeliveryNoteExtraction = z.infer<typeof deliveryNoteExtractionSchema>;

/**
 * Decision 137 item 23: Wildswinkel's "Funds Release Note". It evidences that the animals of the lots it names were
 * delivered and accepted, so the auction house released the seller's funds. amount_incl is the amount released,
 * including VAT, as printed: it is not a payment by Royal Kalahari and records none. release_date "" when none is
 * printed. The lot numbers exactly as printed, at least one.
 */
export const fundsReleaseNoteExtractionSchema = z.object({
  doc_type: z.literal("funds_release_note"),
  issuer: party,
  note_number: text,
  release_date: dateOrBlank,
  auction_name: text,
  invoice_reference: text.optional().default(""),
  lot_numbers: z.array(z.string().min(1)).min(1),
  amount_incl: rand,
  buyer: party,
  signature_present: z.boolean(),
  ...readingTail
});
export type FundsReleaseNoteExtraction = z.infer<typeof fundsReleaseNoteExtractionSchema>;

/**
 * A bank's notice or proof of payment (BD-81). payee_* are the beneficiary's bank details as printed; the
 * beneficiary account number is only fingerprinted (A-08). reference is the beneficiary reference, which is how a
 * payment is matched to an invoice; bank_reference is the bank's own reference number for the transaction.
 */
export const proofOfPaymentExtractionSchema = z.object({
  doc_type: z.literal("proof_of_payment"),
  issued_by: text.optional().default(""),
  notice_not_confirmation: z.boolean().optional().default(false),
  payer: text,
  debit_account_name: text.optional().default(""),
  payee: text,
  payee_bank: text.optional().default(""),
  payee_branch_code: text.optional().default(""),
  payment_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  amount: rand,
  reference: text,
  bank_reference: text.optional().default(""),
  bank_account_number_for_fingerprint_only: text.optional().default(""),
  ...readingTail
});
export type ProofOfPaymentExtraction = z.infer<typeof proofOfPaymentExtractionSchema>;

/**
 * A seller's capture evidence for one animal (BD-83): a darting checklist, a microchip packet, or a photo of the
 * animal, its tag or a horn measurement. Values are copied as written; anything the reader is unsure of is named in
 * unsure_fields and never guessed. Horn lengths stay as written ("25 6/8\"").
 */
const horns = z.object({ right: text, left: text, right_base: text, left_base: text, right_tip: text, left_tip: text, tip_to_tip: text }).partial();
export const CAPTURE_KINDS = ["darting_checklist", "microchip_packet", "tag_photo", "animal_photo", "horn_measurement_photo", "transport_permit", "vet_health_certificate", "other"] as const;
export const captureEvidenceExtractionSchema = z.object({
  doc_type: z.literal("capture_evidence"),
  kind: z.enum(CAPTURE_KINDS),
  issued_by: text.optional().default(""),
  capture_date: z.union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).optional().default(""),
  species_raw: text.optional().default(""),
  sex: z.enum(["M", "F", ""]).optional().default(""),
  tag_colour: text.optional().default(""),
  tag_number: text.optional().default(""),
  animal_name: text.optional().default(""),
  sire: text.optional().default(""),
  veterinarian: text.optional().default(""),
  microchip_on_form: text.optional().default(""),
  chip_number: text.optional().default(""),
  chip_standard: text.optional().default(""),
  horns: horns.optional().default({}),
  checklist_boxes: z.object({ tagged: z.boolean(), capped: z.boolean(), dipped: z.boolean(), teeth: z.boolean(), dna: z.boolean() }).nullable().optional().default(null),
  tag_written_by_hand: z.boolean().optional().default(false),
  documents_seen: z.array(z.string()).optional().default([]),
  unsure_fields: z.array(z.string()).optional().default([]),
  ...readingTail
});
export type CaptureEvidenceExtraction = z.infer<typeof captureEvidenceExtractionSchema>;

export const extractionSchema = z.discriminatedUnion("doc_type", [
  invoiceExtractionSchema, creditNoteExtractionSchema, sellerInvoiceExtractionSchema, deliveryNoteExtractionSchema, proofOfPaymentExtractionSchema, captureEvidenceExtractionSchema,
  fundsReleaseNoteExtractionSchema
]);
export type Extraction = z.infer<typeof extractionSchema>;

/** The kinds of document the reader extracts in full; the others are classified and filed. */
export const READ_TYPES: DocType[] = ["purchase_invoice", "credit_note", "seller_invoice", "delivery_note", "proof_of_payment", "capture_evidence", "funds_release_note"];

/** JSON Schema of each extraction, given to the reader model as its output format. */
export function extractionJsonSchema(docType: DocType): Record<string, unknown> {
  const money = { type: "number", description: "Rand, as printed, e.g. 1656000.00" };
  const s = { type: "string" };
  // Decision 77 7(b) b1: the page a fact is printed on, counted from 1; null only when it truly cannot be told.
  const pageJ = (what: string) => ({ type: ["integer", "null"], description: `The 1-based page of the file on which ${what} is printed` });
  const partyJ = { type: "object", properties: { name: s, vat_no: s }, required: ["name", "vat_no"], additionalProperties: false };
  const contactJ = { type: "object", properties: { name: s, phone: s, email: s, vat_no: s }, required: ["name", "phone", "email", "vat_no"], additionalProperties: false };
  const lineJ = {
    type: "object",
    properties: {
      lot_number: { type: "string", description: "Exactly as printed, e.g. 069, 004A, 72" },
      species_raw: { type: "string", description: "The species words exactly as printed, e.g. 'Oryx - Common'" },
      males: { type: "integer" }, females: { type: "integer" }, unknown_sex: { type: "integer" },
      unit_price_excl: { type: ["number", "null"], description: "Printed unit price, or null" },
      price_excl: money, vat_rate: { type: "number", description: "0.15 or 0" },
      description_raw: { type: "string", description: "The lot description copied exactly, joined across page breaks" },
      seller: contactJ,
      tags_raw: { type: "array", items: s },
      page: pageJ("this lot's price (the page where the lot starts if it runs over a page break)")
    },
    required: ["lot_number", "species_raw", "males", "females", "unknown_sex", "unit_price_excl", "price_excl", "vat_rate", "description_raw", "seller", "tags_raw", "page"],
    additionalProperties: false
  };
  const common = { pages_read: { type: "integer" }, page: pageJ("the document's totals (for a payment: the payment itself)"), confidence: { type: "number" }, notes: s };
  const obj = (props: Record<string, unknown>) => ({ type: "object", properties: { ...props, ...common }, required: [...Object.keys(props), ...Object.keys(common)], additionalProperties: false });
  switch (docType) {
    case "purchase_invoice":
      return obj({ doc_type: { type: "string", enum: ["purchase_invoice"] }, issuer: partyJ, invoice_number: s,
        invoice_date: { type: "string", description: "YYYY-MM-DD as printed; the auction date when only that is printed (auction_date_used true); empty when neither is printed" },
        auction_date: { type: "string", description: "The auction's date, YYYY-MM-DD, or empty when none is printed" },
        auction_date_used: { type: "boolean", description: "True only when invoice_date is the auction date because no invoice date is printed" }, auction_name: s,
        addressed_to: partyJ, terms_wording: s, lines: { type: "array", items: lineJ }, total_excl: money, vat: money, total_incl: money,
        balance_due: { type: ["number", "null"] }, bank_name: s, bank_branch_code: s, bank_account_number_for_fingerprint_only: s });
    case "credit_note": {
      // Decision 88 #1: a credit line keeps its printed price per animal (unit_price_excl); the tags are left out.
      const { tags_raw: _t, ...rest } = lineJ.properties;
      const cl = { ...lineJ, properties: { ...rest, unit_price_excl: { type: ["number", "null"], description: "The printed price per animal excluding VAT, or null" } },
        required: lineJ.required.filter((k) => k !== "tags_raw") };
      return obj({ doc_type: { type: "string", enum: ["credit_note"] }, issuer: partyJ, credit_number: s, credit_date: { type: "string", description: "YYYY-MM-DD" }, auction_name: s,
        invoice_reference: s, addressed_to: partyJ, lines: { type: "array", items: cl }, total_excl: money, vat: money, total_incl: money });
    }
    case "seller_invoice":
      return obj({ doc_type: { type: "string", enum: ["seller_invoice"] }, seller: contactJ, document_number: s, document_date: { type: "string", description: "YYYY-MM-DD" }, addressed_to: partyJ,
        lines: { type: "array", items: { type: "object", properties: { lot_number: s, species_raw: s, sex: { type: "string", enum: ["M", "F", "U"] }, price_excl: money, description_raw: s },
          required: ["lot_number", "species_raw", "sex", "price_excl", "description_raw"], additionalProperties: false } },
        commission_excl: { type: ["number", "null"] }, total_excl: money, vat: money, total_incl: money, bank_account_number_for_fingerprint_only: s });
    case "delivery_note":
      return obj({ doc_type: { type: "string", enum: ["delivery_note"] }, issuer: partyJ, note_number: s, delivery_date: { type: "string", description: "YYYY-MM-DD" }, invoice_reference: s,
        signature_present: { type: "boolean" },
        items: { type: "array", items: { type: "object", properties: { lot_number: s, species_raw: s, sex: { type: "string", enum: ["M", "F", "U"] }, tag: s, count: { type: "integer" },
          arrived: { type: "string", enum: ["alive", "dead"] }, accepted: { type: "string", enum: ["yes", "no", "unknown"] }, reason: s },
          required: ["lot_number", "species_raw", "sex", "tag", "count", "arrived", "accepted", "reason"], additionalProperties: false } } });
    case "proof_of_payment":
      return obj({ doc_type: { type: "string", enum: ["proof_of_payment"] }, issued_by: s, notice_not_confirmation: { type: "boolean" }, payer: s, debit_account_name: s,
        payee: s, payee_bank: s, payee_branch_code: s, payment_date: { type: "string", description: "YYYY-MM-DD" }, amount: money, reference: s, bank_reference: s,
        bank_account_number_for_fingerprint_only: s });
    case "capture_evidence": {
      const hornJ = { type: "object", properties: Object.fromEntries(["right", "left", "right_base", "left_base", "right_tip", "left_tip", "tip_to_tip"].map((k) => [k, s])),
        required: ["right", "left", "right_base", "left_base", "right_tip", "left_tip", "tip_to_tip"], additionalProperties: false };
      const boxJ = { type: ["object", "null"], properties: Object.fromEntries(["tagged", "capped", "dipped", "teeth", "dna"].map((k) => [k, { type: "boolean" }])),
        required: ["tagged", "capped", "dipped", "teeth", "dna"], additionalProperties: false };
      return obj({ doc_type: { type: "string", enum: ["capture_evidence"] }, kind: { type: "string", enum: [...CAPTURE_KINDS] }, issued_by: s,
        capture_date: { type: "string", description: "YYYY-MM-DD, or empty" }, species_raw: s, sex: { type: "string", enum: ["M", "F", ""] }, tag_colour: s, tag_number: s,
        animal_name: s, sire: s, veterinarian: s, microchip_on_form: s, chip_number: s, chip_standard: s, horns: hornJ, checklist_boxes: boxJ,
        tag_written_by_hand: { type: "boolean" }, documents_seen: { type: "array", items: s }, unsure_fields: { type: "array", items: s } });
    }
    case "funds_release_note":
      // Decision 137 item 23: delivery and acceptance evidence for the lots named; the amount released is not a payment.
      return obj({ doc_type: { type: "string", enum: ["funds_release_note"] }, issuer: partyJ,
        note_number: { type: "string", description: "The note's own number or reference, exactly as printed; empty when none is printed" },
        release_date: { type: "string", description: "The date of the release as printed, YYYY-MM-DD; empty when none is printed" },
        auction_name: { type: "string", description: "The auction the lots were sold at, as printed" },
        invoice_reference: { type: "string", description: "The invoice number the note names, exactly as printed; empty when none is printed" },
        lot_numbers: { type: "array", items: { type: "string", description: "A lot number exactly as printed, e.g. 101, 071A" }, description: "Every lot the note releases funds for, exactly as printed" },
        amount_incl: { ...money, description: "The amount released to the seller, in rand including VAT, as printed. It is not a payment by the buyer and is never recorded as one." },
        buyer: partyJ, signature_present: { type: "boolean" } });
    default:
      return obj({ doc_type: { type: "string", enum: [docType] } });
  }
}
